// ============================================
// Path: /frontend/src/pages/admin/InquiriesPage.jsx
// Purpose: Admin page to view and manage listing inquiries
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { inquiriesAPI } from '../../api/inquiries.api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Eye, MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <CardContent>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Listing Title</TableHead>
          <TableHead>Inquirer's Email</TableHead>
          <TableHead>Phone</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className="h-5 w-48" /></TableCell>
            <TableCell><Skeleton className="h-5 w-40" /></TableCell>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function InquiriesPage() {
  console.log('🎨 [InquiriesPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchInquiries = async () => {
      console.log('🔵 [InquiriesPage] Fetching inquiries...');
      setLoading(true);

      try {
        const data = await inquiriesAPI.getAllInquiries();
        console.log('✅ [InquiriesPage] Inquiries received:', data);
        setInquiries(data.inquiries || []);
        setError(null);
      } catch (err) {
        console.error('❌ [InquiriesPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load inquiries');
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, []);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleViewDetails = (inquiry) => {
    console.log('🔵 [InquiriesPage] Opening inquiry details:', inquiry.id);
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    console.log('🔵 [InquiriesPage] Closing inquiry details');
    setIsModalOpen(false);
    setSelectedInquiry(null);
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Listing Inquiries</CardTitle>
          <CardDescription>
            Track user inquiries made on vehicle and property listings.
          </CardDescription>
        </CardHeader>

        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <CardContent>
            <p className="text-destructive">Error loading inquiries: {error}</p>
          </CardContent>
        ) : (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Listing Title</TableHead>
                  <TableHead>Inquirer's Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries?.map((inquiry) => (
                  <TableRow key={inquiry.id}>
                    <TableCell className="font-medium">{inquiry.listingTitle}</TableCell>
                    <TableCell>{inquiry.inquirerEmail}</TableCell>
                    <TableCell>{inquiry.phone || 'N/A'}</TableCell>
                    <TableCell>
                      {inquiry.createdAt ? format(new Date(inquiry.createdAt), 'PPP') : 'N/A'}
                    </TableCell>
                    <TableCell className="flex justify-center items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        title="View Message"
                        onClick={() => handleViewDetails(inquiry)}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Link to={`/listing/${inquiry.listingId}`}>
                        <Button variant="ghost" size="icon" title="View Listing">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {inquiries?.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No inquiries found.</p>
            )}
          </CardContent>
        )}
      </Card>

      {/* INQUIRY DETAILS MODAL */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
            <DialogDescription>
              Full details of the inquiry message
            </DialogDescription>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-1">Listing</h4>
                <p className="text-base">{selectedInquiry.listingTitle}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-1">Inquirer Email</h4>
                  <p className="text-base">{selectedInquiry.inquirerEmail}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-1">Phone</h4>
                  <p className="text-base">{selectedInquiry.phone || 'Not provided'}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-1">Date</h4>
                <p className="text-base">
                  {selectedInquiry.createdAt ? format(new Date(selectedInquiry.createdAt), 'PPP p') : 'N/A'}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-1">Message</h4>
                <div className="bg-muted p-4 rounded-md">
                  <p className="text-base whitespace-pre-wrap">
                    {selectedInquiry.message || 'No message provided'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Link to={`/listing/${selectedInquiry.listingId}`}>
                  <Button variant="outline">
                    <Eye className="h-4 w-4 mr-2" />
                    View Listing
                  </Button>
                </Link>
                <Button onClick={handleCloseModal}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}